import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavigationProfessionnels from '@/components/NavigationProfessionnels';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  Bike,
  Wrench,
  Users,
  ArrowRight,
  Tent,
  Truck,
  Calendar,
  Eye,
  Building,
  UserCheck,
  MapPin,
  Shield,
  CheckCircle,
  Heart,
  Settings,
  Recycle,
  Search
} from 'lucide-react';

const NosServices = () => {
  // SERVICES POUR PARTICULIERS
  const servicesParticuliers = [
    {
      id: 'location-campings',
      title: 'Location dans les campings',
      subtitle: 'Service intégré',
      description: 'Location directe dans votre camping partenaire. Réservation simple, vélos entretenus, service sur place.',
      benefit: 'Réservation en ligne ou sur place • Vélos adaptés au secteur • Maintenance incluse • Service intégré au camping',
      icon: Building,
      image: './images/camping_hero_illustration_20260211_111332.png',
      cta: 'Réserver un vélo',
      link: '/reserver'
    },
    {
      id: 'livraison-cote',
      title: 'Livraison sur la côte atlantique',
      subtitle: 'Service à domicile',
      description: 'Livraison de vélos directement sur votre lieu de vacances.',
      benefit: 'Livraison à domicile ou hébergement • Disponibilité selon zone • Service clé en main',
      icon: Truck,
      image: './images/delivery_service_illustration_20260211_111332.png',
      cta: 'Demander une livraison',
      link: '/demande-livraison'
    },
    {
      id: 'velos-reconditionnes',
      title: 'Vélos reconditionnés',
      subtitle: 'Seconde vie',
      description: 'Nos vélos de location trouvent une seconde vie. Révisés, contrôlés, prêts pour de nouvelles années d\'utilisation.',
      benefit: 'Révision complète • Garantie qualité • Démarche responsable',
      icon: Recycle,
      image: './images/image00008.jpeg',
      cta: 'Voir les vélos disponibles',
      link: '/velos-occasion'
    },
    {
      id: 'recherche-personnalisee',
      title: 'Recherche personnalisée de vélo',
      subtitle: 'Facilitateur d\'achat',
      description: 'Vous cherchez un modèle spécifique ? Une marque précise ? Nous recherchons pour vous.',
      benefit: 'Les Hirondelles agit comme facilitateur d\'achat : Recherche du modèle demandé • Négociation et proposition tarifaire • Montage et préparation • Livraison possible • Service après-vente assuré',
      icon: Search,
      image: './images/bike_station_hero_20260211_111335.png',
      cta: 'Faire une demande',
      link: '/nous-contacter'
    }
  ];

  // SOLUTIONS POUR PROFESSIONNELS
  const solutionsProfessionnels = [
    {
      id: 'location-mensuelle',
      title: 'Location mensuelle clé en main',
      subtitle: 'Gestion complète',
      description: 'Parc complet exploité, entretien inclus, gestion simplifiée.',
      benefit: 'Commission sur chiffre d\'affaires • Vision des contrats et statistiques',
      icon: Calendar,
      image: './images/camping_hero_illustration_20260211_111332.png',
      cta: 'Découvrir',
      link: '/professionnels'
    },
    {
      id: 'mise-disposition',
      title: 'Parc en mise à disposition',
      subtitle: 'Petits établissements',
      description: '8 à 20 vélos, aucune avance de frais, commissionnement uniquement.',
      benefit: 'Activation simple et flexible',
      icon: Users,
      image: './images/workshop_professional_20260211_111332.png',
      cta: 'En savoir plus',
      link: '/professionnels'
    },
    {
      id: 'achat-maintenance',
      title: 'Achat de parc + maintenance',
      subtitle: 'Solution propriétaire',
      description: 'Possibilité d\'achat de vélos optimisés, entretien fin de saison, accompagnement technique.',
      benefit: 'Propriété du parc avec expertise technique',
      icon: Settings,
      image: './images/corporate_group_cycling_20260211_111333.png',
      cta: 'Nous contacter',
      link: '/achat-parcs-velo'
    }
  ];

  const allServices = [...servicesParticuliers, ...solutionsProfessionnels];

  return (
    <div className="min-h-screen bg-white">
      <NavigationProfessionnels />
      
      {/* Header Premium Vert */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Nos Services
              </h1>
              <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 leading-relaxed">
                La coopérative Les Hirondelles vous accompagne avec expertise et professionnalisme 
                dans tous vos projets vélo sur la côte atlantique.
              </p>
              
              {/* Points clés avec icônes */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center mr-4">
                    <Bike className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="text-lg">Location flexible</span>
                </div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center mr-4">
                    <Wrench className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="text-lg">Maintenance incluse</span>
                </div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center mr-4">
                    <Users className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="text-lg">Service client dédié</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="./images/bike_station_hero_20260211_111335.png" 
                alt="Services vélo Les Hirondelles"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Titre Particuliers */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <h2 className="text-3xl font-bold text-black mb-4">
              1️⃣ Services pour Particuliers
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Des solutions adaptées à vos besoins de mobilité sur la côte atlantique
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Particuliers */}
      {servicesParticuliers.map((service, index) => (
        <section key={service.id} className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
              index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'
            }`}>
              <ScrollReveal className={index % 2 === 0 ? '' : 'lg:col-start-2'}>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <service.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-primary uppercase tracking-wide">
                        {service.subtitle}
                      </div>
                      <h3 className="text-2xl font-bold text-black">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="bg-primary/5 rounded-lg p-6">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
                      <p className="text-slate-700 leading-relaxed">
                        {service.benefit}
                      </p>
                    </div>
                  </div>
                  
                  <Button asChild size="lg" className="btn-primary">
                    <Link to={service.link}>
                      {service.cta}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={200} className={index % 2 === 0 ? '' : 'lg:col-start-1'}>
                <div className="relative">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-96 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      ))}

      {/* Section Titre Professionnels */}
      <section className="py-12 bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <h2 className="text-3xl font-bold mb-4">
              2️⃣ Solutions pour Professionnels
            </h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto mb-8">
              Les Hirondelles accompagne les campings et hébergements touristiques avec des solutions 
              adaptées à leur taille et à leur modèle économique.
            </p>
            <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <p className="text-lg text-white/95 font-medium">
                Les Hirondelles n'est pas seulement un loueur de vélos, 
                mais un facilitateur d'activité pour les établissements touristiques.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Solutions Professionnels */}
      {solutionsProfessionnels.map((service, index) => (
        <section key={service.id} className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
              index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'
            }`}>
              <ScrollReveal className={index % 2 === 0 ? '' : 'lg:col-start-2'}>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <service.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-primary uppercase tracking-wide">
                        {service.subtitle}
                      </div>
                      <h3 className="text-2xl font-bold text-black">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="bg-primary/5 rounded-lg p-6">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
                      <p className="text-slate-700 leading-relaxed">
                        {service.benefit}
                      </p>
                    </div>
                  </div>
                  
                  <Button asChild size="lg" className="btn-primary">
                    <Link to={service.link}>
                      {service.cta}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={200} className={index % 2 === 0 ? '' : 'lg:col-start-1'}>
                <div className="relative">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-96 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      ))}

      {/* Section Expertise */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <div className="bg-white rounded-2xl p-12 border border-black/10">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-primary" />
              </div>
              
              <h2 className="text-2xl font-bold text-black mb-6">
                Un partenaire mobilité de confiance
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto mb-8">
                Les Hirondelles se positionne comme votre partenaire mobilité de confiance, 
                pas seulement comme un loueur de vélos. Nous facilitons l'accès au vélo 
                pour tous, particuliers comme professionnels.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">7+</div>
                  <div className="text-slate-600">Services différents</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">B2C + B2B</div>
                  <div className="text-slate-600">Double expertise</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">24h/7j</div>
                  <div className="text-slate-600">Support technique</div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default NosServices;